import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ClipboardList, Inbox, Eye, ShieldCheck, BadgeCheck, PlusCircle } from 'lucide-react';
import AdCreditsWidget from '@/components/marketplace/AdCreditsWidget';

const StatCard = ({ label, value, icon: Icon, hint, to, testId }) => (
  <Link to={to || '#'} className='block' data-testid={testId}>
    <Card className='p-5 hover:shadow-md transition-shadow'>
      <div className='flex items-start justify-between'>
        <div>
          <div className='text-xs uppercase text-muted-foreground tracking-wider'>{label}</div>
          <div className='text-2xl font-semibold mt-1 tabular-nums'>{value}</div>
          {hint && <div className='text-xs text-muted-foreground mt-1'>{hint}</div>}
        </div>
        <div className='h-9 w-9 rounded-lg bg-secondary flex items-center justify-center'><Icon className='h-4 w-4 text-primary' /></div>
      </div>
    </Card>
  </Link>
);

const DashboardHome = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({ active: 0, pending: 0, inquiries_new: 0, reveals: 0 });
  const [recentInquiries, setRecentInquiries] = useState([]);

  useEffect(() => {
    const load = () => {
      api.get('/listings/mine').then(({ data }) => {
        setStats(s => ({
          ...s,
          active: data.filter(l => l.status === 'live' || l.status === 'approved').length,
          pending: data.filter(l => l.status === 'pending_review' || l.status === 'submitted').length,
        }));
      });
      api.get('/inquiries/received').then(({ data }) => {
        setStats(s => ({ ...s, inquiries_new: data.filter(i => i.status === 'new').length }));
        setRecentInquiries(data.slice(0, 5));
      });
      api.get('/inquiries/reveal/on-my-listings').then(({ data }) => {
        setStats(s => ({ ...s, reveals: data.length }));
      });
    };
    load();
    // #10: Refresh dashboard tiles when tab regains focus so approved
    // listings stop showing as pending after a moderator action.
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  return (
    <div className='space-y-6'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div>
          <h1 className='text-2xl sm:text-3xl font-semibold tracking-tight'>Welcome, {user?.name?.split(' ')[0]}</h1>
          <p className='text-muted-foreground text-sm'>Here&apos;s an overview of your IndusFact activity.</p>
        </div>
        <div className='flex flex-wrap gap-2'>
          <Link to='/post'><Button data-testid='dashboard-post-listing-cta' className='bg-accent text-accent-foreground hover:bg-accent/90'><PlusCircle className='h-4 w-4 mr-1.5' /> Post new listing</Button></Link>
          <Link to='/dashboard/listings'><Button variant='outline'>My listings</Button></Link>
        </div>
      </div>

      <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
        <StatCard label='Active listings' value={stats.active} icon={ClipboardList} to='/dashboard/listings' testId='dashboard-stat-active' />
        <StatCard label='Pending approval' value={stats.pending} icon={ShieldCheck} to='/dashboard/listings' testId='dashboard-stat-pending' />
        <StatCard label='New inquiries' value={stats.inquiries_new} icon={Inbox} to='/dashboard/inquiries' testId='dashboard-stat-inquiries' />
        <StatCard label='Contact reveals' value={stats.reveals} icon={Eye} to='/dashboard/reveal-log' testId='dashboard-stat-reveals' />
      </div>

      <div className='grid lg:grid-cols-3 gap-4'>
        <Card className='p-6 lg:col-span-2'>
          <div className='flex items-center justify-between mb-3'>
            <div className='font-semibold'>Recent inquiries</div>
            <Link to='/dashboard/inquiries' className='text-xs text-primary hover:underline'>See all</Link>
          </div>
          {recentInquiries.length === 0 ? (
            <div className='text-sm text-muted-foreground'>No inquiries yet. Once your listings are live, buyer inquiries will appear here.</div>
          ) : (
            <div className='space-y-3'>
              {recentInquiries.map(i => (
                <div key={i.id} className='flex items-start justify-between gap-3 border-b pb-3 last:border-b-0'>
                  <div>
                    <div className='font-medium text-sm'>{i.inquirer_name} <span className='text-muted-foreground'>· {i.inquirer_company || 'Individual'}</span></div>
                    <div className='text-xs text-muted-foreground truncate max-w-md'>On: {i.listing_title}</div>
                    <div className='text-sm mt-1'>{i.message.slice(0, 120)}{i.message.length > 120 ? '…' : ''}</div>
                  </div>
                  <div className='text-xs text-muted-foreground shrink-0'>{new Date(i.created_at).toLocaleDateString()}</div>
                </div>
              ))}
            </div>
          )}
        </Card>
        <Card className='p-6'>
          <div className='font-semibold mb-2 flex items-center gap-2'><BadgeCheck className='h-4 w-4 text-[hsl(var(--verified))]' /> Verification status</div>
          <div className='text-sm text-muted-foreground mb-3'>Current level: <span className='font-semibold text-foreground'>{user?.verification_level?.replace('_', ' ')}</span></div>
          {user?.verification_level !== 'company_verified' && (
            <Link to='/dashboard/verification'><Button variant='outline' size='sm' data-testid='dashboard-verify-cta'>Get verified</Button></Link>
          )}
        </Card>
      </div>

      <AdCreditsWidget />
    </div>
  );
};

export default DashboardHome;
